/**
 * The arithmetic of shrinking a photo, with no DOM in it.
 *
 * Split out from `compressImage.ts` deliberately: jsdom has no canvas, no
 * `createImageBitmap` and no `URL.createObjectURL`, so anything that touches those can
 * only be tested against a stub of our own making — which tests the stub. Every decision
 * that could actually be wrong lives here instead, where it is ordinary arithmetic and
 * can be tested for real.
 */

/** Longest edge we keep. A shelf label is still readable; a 12MP original is not needed. */
export const LongEdge = 1200;

/** What we aim for. Roughly a second on a bad connection. */
export const TargetBytes = 80 * 1024;

/**
 * The hard ceiling, and the point of giving up.
 *
 * Must equal `ItemImage.MaxBytes` on the server (Features/Items/ItemImage.cs). If this
 * were the larger of the two, the app would spend a phone's battery compressing something
 * the server then refuses.
 */
export const MaxBytes = 150 * 1024;

/**
 * Tried in order. Starts where a photograph still looks like a photograph — below about
 * 0.4 a shelf label stops being readable, which is the one thing the photo is for.
 */
export const QualityLadder = [0.72, 0.6, 0.5, 0.42] as const;

/** How far the long edge may be pulled in when the ladder alone is not enough. */
const MinLongEdge = 800;

export interface Size {
  width: number;
  height: number;
}

/**
 * The size to draw at: the long edge brought down to `longEdge`, aspect ratio kept.
 *
 * Never upscales. Someone attaching a small picture of a label should get their small
 * picture back, not a blurry enlargement of it that costs more to store.
 */
export function planResize(width: number, height: number, longEdge: number = LongEdge): Size {
  const longest = Math.max(width, height);

  if (longest <= longEdge || longest === 0) {
    return { width, height };
  }

  const scale = longEdge / longest;

  // At least 1px in each direction: a 4000×3 panorama must not round to zero height,
  // which produces a canvas that throws rather than an image that is merely odd.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Intermediate sizes to step through when shrinking a long way.
 *
 * Browsers resample with a small kernel, so going from 4000px to 1200px in one draw
 * samples about one pixel in three and the result is visibly mushy — worst of all on
 * text, which is exactly what someone photographs a shelf label for. Halving repeatedly
 * until within 2× of the target keeps every pixel contributing.
 */
export function halvingSteps(from: number, to: number): number[] {
  const steps: number[] = [];

  let current = from;
  while (current / 2 > to) {
    current = Math.round(current / 2);
    steps.push(current);
  }

  return steps;
}

/**
 * WebP where it exists, JPEG otherwise.
 *
 * This is not a preference, it is a correctness guard. `canvas.toBlob` with a type the
 * browser cannot encode does not fail — it silently returns PNG, and a 1200px PNG of a
 * photograph is well over a megabyte, so every attach would be refused by the size cap on
 * that platform and nowhere else.
 */
export function chooseType(canEncodeWebp: boolean): 'image/webp' | 'image/jpeg' {
  return canEncodeWebp ? 'image/webp' : 'image/jpeg';
}

export interface Attempt {
  longEdge: number;
  quality: number;
}

export interface AttemptState {
  attempt: Attempt;
  bytes: number;
}

/**
 * What to do after an encode: accept it, try again smaller, or admit defeat.
 *
 * Walks the quality ladder first and only then pulls the dimensions in, because losing
 * quality is less damaging to a photo of a label than losing pixels.
 */
export function nextAttempt(state: AttemptState): Attempt | 'accept' | 'give-up' {
  if (state.bytes <= TargetBytes) {
    return 'accept';
  }

  const nextQuality = QualityLadder[QualityLadder.indexOf(state.attempt.quality as never) + 1];

  if (nextQuality !== undefined) {
    return { longEdge: state.attempt.longEdge, quality: nextQuality };
  }

  const smaller = Math.round(state.attempt.longEdge * 0.8);

  if (smaller >= MinLongEdge) {
    return { longEdge: smaller, quality: QualityLadder[0] };
  }

  // Out of ideas. Under the hard cap it is still worth keeping — the target is an aim,
  // the cap is the rule — and over it there is nothing to be done.
  return state.bytes <= MaxBytes ? 'accept' : 'give-up';
}

/**
 * Whether an encode produced something implausible for its pixel count.
 *
 * Safari returns a blank, fully transparent canvas — with no error at all — above roughly
 * 16.7 million output pixels, which a modern phone photo reaches. A blank canvas encodes
 * to almost nothing, so the size is the only signal available, and without this check the
 * app would cheerfully store an empty rectangle and say it worked.
 */
export function looksBlank(bytes: number, width: number, height: number): boolean {
  const pixels = width * height;

  if (pixels < 10_000) {
    return false;
  }

  // Even a photograph of a plain wall carries far more than this per pixel.
  return bytes < pixels / 400;
}
